import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { 
  CreditCard, Calendar, XCircle, 
  Shield, AlertTriangle, Scale, Phone, Mail,
  ChevronLeft, ChevronRight
} from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const CGVPage = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);

  const sections = [
    { id: "tarifs", icon: CreditCard, labelKey: "cgv.tabs.tarifs", titleKey: "cgv.tarifs.title" },
    { id: "reservation", icon: Calendar, labelKey: "cgv.tabs.reservation", titleKey: "cgv.reservation.title" },
    { id: "paiement", icon: CreditCard, labelKey: "cgv.tabs.paiement", titleKey: "cgv.paiement.title" },
    { id: "annulation", icon: XCircle, labelKey: "cgv.tabs.annulation", titleKey: "cgv.annulation.title" },
    { id: "assurance", icon: Shield, labelKey: "cgv.tabs.assurance", titleKey: "cgv.assurance.title" },
    { id: "responsabilite", icon: AlertTriangle, labelKey: "cgv.tabs.responsabilite", titleKey: "cgv.responsabilite.title" },
    { id: "litiges", icon: Scale, labelKey: "cgv.tabs.litiges", titleKey: "cgv.litiges.title" },
  ];

  const canGoPrev = activeTab > 0;
  const canGoNext = activeTab < sections.length - 1;

  const renderContent = () => {
    switch (activeTab) {
      case 0: return <TarifsContent t={t} />;
      case 1: return <ReservationContent t={t} />;
      case 2: return <PaiementContent t={t} />;
      case 3: return <AnnulationContent t={t} />;
      case 4: return <AssuranceContent t={t} />;
      case 5: return <ResponsabiliteContent t={t} />;
      case 6: return <LitigesContent t={t} />;
      default: return null;
    }
  };

  return (
    <>
      <SEOHead 
        title={`${t("cgv.pageTitle")} | XploreSahara`}
        description={t("cgv.pageTitle")}
        keywords="CGV, conditions générales vente, réservation trek, annulation voyage désert"
      />
      
      <div className="min-h-screen pt-20 md:pt-24 pb-12 md:pb-20" data-testid="cgv-page">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8 md:mb-10">
            <span className="text-primary font-medium tracking-widest uppercase text-xs md:text-sm">
              {t("cgv.subtitle")}
            </span>
            <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold mt-2">
              {t("cgv.pageTitle")}
            </h1>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-border overflow-hidden">
            
            {/* Tab Headers */}
            <div className="border-b border-border">
              <div className="flex overflow-x-auto scrollbar-hide">
                {sections.map((section, index) => {
                  const Icon = section.icon;
                  const isActive = activeTab === index;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveTab(index)}
                      className={cn(
                        "flex items-center justify-center gap-1.5 px-3 md:px-4 py-3 md:py-4 text-xs md:text-sm font-medium whitespace-nowrap transition-all border-b-2 -mb-[2px] flex-1 min-w-[50px]",
                        isActive
                          ? "border-primary text-primary bg-primary/5"
                          : "border-transparent text-muted-foreground hover:text-foreground"
                      )}
                      data-testid={`tab-${section.id}`}
                    >
                      <Icon className="h-4 w-4 flex-shrink-0" />
                      <span className="hidden sm:inline">{t(section.labelKey)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="h-1 bg-gray-100">
              <div 
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${((activeTab + 1) / sections.length) * 100}%` }}
              />
            </div>

            {/* Tab Content */}
            <div className="p-4 md:p-6 lg:p-8 min-h-[350px] md:min-h-[400px]">
              {/* Section Title */}
              <div className="flex items-center gap-3 mb-5 md:mb-6">
                <span className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary text-primary-foreground font-bold text-sm md:text-base">
                  {activeTab + 1}
                </span>
                <h2 className="text-lg md:text-xl lg:text-2xl font-bold">
                  {t(sections[activeTab].titleKey)}
                </h2>
              </div>

              {/* Section Content */}
              <div className="text-muted-foreground leading-relaxed text-sm md:text-base">
                {renderContent()}
              </div>
            </div>

            {/* Navigation Footer */}
            <div className="border-t border-border p-3 md:p-4">
              <div className="flex items-center justify-between gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveTab(prev => prev - 1)}
                  disabled={!canGoPrev}
                  className="gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-4"
                  data-testid="prev-btn"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="hidden xs:inline">{t("cgv.nav.previous")}</span>
                </Button>

                <div className="flex items-center gap-1 md:gap-1.5">
                  {sections.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveTab(index)}
                      className={cn(
                        "w-2 h-2 md:w-2.5 md:h-2.5 rounded-full transition-all",
                        activeTab === index 
                          ? "bg-primary w-4 md:w-6" 
                          : "bg-gray-300 hover:bg-gray-400"
                      )}
                      aria-label={`Section ${index + 1}`}
                    />
                  ))}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveTab(prev => prev + 1)}
                  disabled={!canGoNext}
                  className="gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-4"
                  data-testid="next-btn"
                >
                  <span className="hidden xs:inline">{t("cgv.nav.next")}</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Contact Card */}
          <div className="mt-8 md:mt-10 bg-gradient-to-r from-primary to-accent rounded-xl md:rounded-2xl p-5 md:p-8 text-white text-center">
            <h3 className="font-serif text-lg md:text-xl lg:text-2xl font-bold mb-2 md:mb-3">
              {t("cgv.contact.title")}
            </h3>
            <p className="text-white/80 mb-4 md:mb-6 text-sm md:text-base">
              {t("cgv.contact.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4">
              <a 
                href="mailto:contact@saharatrek.org" 
                className="inline-flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 px-4 md:px-5 py-2 md:py-2.5 rounded-full transition-colors text-xs md:text-sm"
              >
                <Mail className="h-4 w-4" />
                contact@saharatrek.org
              </a>
              <a 
                href="tel:+212637346823" 
                className="inline-flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 px-4 md:px-5 py-2 md:py-2.5 rounded-full transition-colors text-xs md:text-sm"
              >
                <Phone className="h-4 w-4" />
                +212 637-346823
              </a>
            </div>
          </div>

          <p className="text-center text-xs md:text-sm text-muted-foreground mt-6 md:mt-8">
            {t("cgv.lastUpdate")} : Mars 2025
          </p>
        </div>
      </div>
    </>
  );
};

// Content Components
const TarifsContent = ({ t }) => (
  <div className="space-y-4">
    <p>
      {t("cgv.tarifs.intro")} <strong className="text-foreground">{t("cgv.tarifs.priceInfo")}</strong>, {t("cgv.tarifs.groupInfo")}
    </p>
    
    <div className="bg-green-50 border border-green-200 rounded-lg p-3 md:p-4">
      <h3 className="font-semibold text-green-800 mb-2 text-sm md:text-base">{t("cgv.tarifs.included.title")}</h3>
      <ul className="list-disc list-inside space-y-1 text-green-700 text-xs md:text-sm">
        {t("cgv.tarifs.included.items", { returnObjects: true }).map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>

    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 md:p-4">
      <h3 className="font-semibold text-amber-800 mb-2 text-sm md:text-base">{t("cgv.tarifs.notIncluded.title")}</h3>
      <ul className="list-disc list-inside space-y-1 text-amber-700 text-xs md:text-sm">
        {t("cgv.tarifs.notIncluded.items", { returnObjects: true }).map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>

    <p>
      <strong className="text-foreground">{t("cgv.tabs.tarifs")} enfant :</strong> {t("cgv.tarifs.childRate")}
    </p>
  </div>
);

const ReservationContent = ({ t }) => (
  <div className="space-y-4">
    <p>
      {t("cgv.reservation.minParticipants")} <strong className="text-foreground">{t("cgv.reservation.minPersons")}</strong>.
    </p>
    
    <p>{t("cgv.reservation.methodsIntro")}</p>
    <ul className="list-disc list-inside space-y-2 ml-2 md:ml-4 text-sm">
      {t("cgv.reservation.methods", { returnObjects: true }).map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>

    <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 md:p-4 mt-4">
      <p className="text-foreground text-sm">
        <strong>{t("cgv.reservation.confirmation.title")}</strong> {t("cgv.reservation.confirmation.text")} <strong>{t("cgv.reservation.confirmation.deposit")}</strong>. {t("cgv.reservation.confirmation.details")}
      </p>
    </div>
  </div>
);

const PaiementContent = ({ t }) => (
  <div className="space-y-4">
    <p>{t("cgv.paiement.intro")}</p>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 md:p-4">
        <h3 className="font-semibold text-foreground mb-2 text-sm md:text-base">{t("cgv.paiement.deposit.title")}</h3>
        <p className="text-xs md:text-sm">{t("cgv.paiement.deposit.text")}</p>
      </div>
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 md:p-4">
        <h3 className="font-semibold text-foreground mb-2 text-sm md:text-base">{t("cgv.paiement.balance.title")}</h3>
        <p className="text-xs md:text-sm">{t("cgv.paiement.balance.text")}</p>
      </div>
    </div>

    <div>
      <p className="font-semibold text-foreground mb-2 text-sm md:text-base">{t("cgv.paiement.methodsTitle")}</p>
      <ul className="list-disc list-inside space-y-1 ml-2 text-xs md:text-sm">
        {t("cgv.paiement.methods", { returnObjects: true }).map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  </div>
);

const AnnulationContent = ({ t }) => {
  const rows = t("cgv.annulation.rows", { returnObjects: true });
  const colors = ["text-green-600", "text-amber-600", "text-orange-600", "text-red-500", "text-red-600"];
  
  return (
    <div className="space-y-4">
      <p>{t("cgv.annulation.intro")}</p>

      <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
        <table className="w-full border-collapse text-xs md:text-sm min-w-[280px]">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-200 px-2 md:px-4 py-2 md:py-3 text-left font-semibold text-foreground">
                {t("cgv.annulation.tableHeaders.delay")}
              </th>
              <th className="border border-gray-200 px-2 md:px-4 py-2 md:py-3 text-left font-semibold text-foreground">
                {t("cgv.annulation.tableHeaders.fees")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className={i % 2 === 1 ? "bg-gray-50" : ""}>
                <td className="border border-gray-200 px-2 md:px-4 py-2 md:py-3">{row.delay}</td>
                <td className={`border border-gray-200 px-2 md:px-4 py-2 md:py-3 font-medium ${colors[i]}`}>
                  {row.fees}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 md:p-4">
        <p className="text-amber-800 text-xs md:text-sm">
          <strong>Important :</strong> {t("cgv.annulation.important")}
        </p>
      </div>

      <p className="text-sm">
        <strong className="text-foreground">{t("cgv.annulation.byUs.title")}</strong> {t("cgv.annulation.byUs.text")}
      </p>
    </div>
  );
};

const AssuranceContent = ({ t }) => (
  <div className="space-y-4">
    <p>{t("cgv.assurance.certifyIntro")}</p>
    <ul className="list-disc list-inside space-y-2 ml-2 md:ml-4 text-sm">
      {t("cgv.assurance.certifyItems", { returnObjects: true }).map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>

    <div className="bg-red-50 border border-red-200 rounded-lg p-3 md:p-4">
      <h3 className="font-semibold text-red-800 mb-2 text-sm md:text-base">{t("cgv.assurance.medical.title")}</h3>
      <p className="text-red-700 text-xs md:text-sm">{t("cgv.assurance.medical.text")}</p>
    </div>

    <p className="text-sm">
      <strong className="text-foreground">{t("cgv.assurance.recommended")}</strong>
    </p>
  </div>
);

const ResponsabiliteContent = ({ t }) => (
  <div className="space-y-4">
    <p>
      <strong className="text-foreground">{t("cgv.responsabilite.program.title")}</strong> {t("cgv.responsabilite.program.text")}
    </p>

    <p>
      <strong className="text-foreground">{t("cgv.responsabilite.belongings.title")}</strong> {t("cgv.responsabilite.belongings.text")}
    </p>

    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 md:p-4">
      <p className="text-foreground text-xs md:text-sm">{t("cgv.responsabilite.waiver")}</p>
    </div>
  </div>
);

const LitigesContent = ({ t }) => (
  <div className="space-y-4">
    <p>
      {t("cgv.litiges.claim.intro")} <strong className="text-foreground">{t("cgv.litiges.claim.delay")}</strong> {t("cgv.litiges.claim.details")}
    </p>

    <p>{t("cgv.litiges.response")}</p>

    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 md:p-4">
      <p className="text-foreground text-xs md:text-sm">{t("cgv.litiges.jurisdiction")}</p>
    </div>

    <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 md:p-4">
      <p className="text-foreground text-sm">
        <strong>{t("cgv.litiges.contactTitle")}</strong><br />
        <span className="text-muted-foreground">Email : contact@saharatrek.org</span><br />
        <span className="text-muted-foreground">{t("cgv.litiges.address")} : M'hamid El Ghizlane, Maroc</span>
      </p>
    </div>
  </div>
);

export default CGVPage;
